<script setup>
import { computed, ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useRoadmapsStore } from '@/stores/roadmaps';
import { formatRange } from '@/lib/schema';
import { hasDates, formatDateRange, rangeDates } from '@/lib/dates';
import { phaseVars } from '@/lib/palette';
import { openProject, openSettings } from '@/composables/useUi';
import Icon from '@/components/ui/Icon.vue';

const props = defineProps({
  roadmap: { type: Object, required: true },
  projectId: { type: String, required: true },
  blockId: { type: String, required: true },
  anchor: { type: Object, required: true }
});

const emit = defineEmits(['close']);
const store = useRoadmapsStore();
const el = ref(null);
const pos = ref({ top: props.anchor.bottom + 8, left: props.anchor.left });

const project = computed(() => props.roadmap.projects.find((p) => p.id === props.projectId));
const block = computed(() => project.value?.blocks.find((b) => b.id === props.blockId));
const dates = computed(() => (block.value && hasDates(props.roadmap) ? formatDateRange(rangeDates(props.roadmap.settings, block.value.start, block.value.span)) : ''));

function setPhase(phaseId) {
  store.updateBlock(props.roadmap.id, props.projectId, props.blockId, { phaseId });
  emit('close');
}

function remove() {
  store.removeBlock(props.roadmap.id, props.projectId, props.blockId);
  emit('close');
}

function editProject() {
  emit('close');
  openProject(props.roadmap.id, props.projectId);
}

function editPhases() {
  emit('close');
  openSettings(props.roadmap.id, 'phases');
}

function place() {
  const box = el.value?.getBoundingClientRect();
  if (!box) return;
  const margin = 10;
  let top = props.anchor.bottom + 8;
  if (top + box.height > window.innerHeight - margin) top = Math.max(margin, props.anchor.top - box.height - 8);
  const left = Math.max(margin, Math.min(props.anchor.left, window.innerWidth - box.width - margin));
  pos.value = { top, left };
}

// Landing page change: the whole window scrolls here, not a .main panel, so close on any
// scroll outside the popover.
const onScroll = (e) => {
  if (!(e.target instanceof Node) || !el.value?.contains(e.target)) emit('close');
};

function onOutside(e) {
  if (el.value && !el.value.contains(e.target)) emit('close');
}

function onKey(e) {
  if (e.key === 'Escape') emit('close');
  if ((e.key === 'Delete' || e.key === 'Backspace') && !/input|textarea/i.test(e.target.tagName)) remove();
}

const close = () => emit('close');

onMounted(() => {
  nextTick(() => {
    place();
    el.value?.querySelector('.phase.current, .phase')?.focus();
  });
  setTimeout(() => window.addEventListener('pointerdown', onOutside, true), 0);
  window.addEventListener('keydown', onKey);
  window.addEventListener('resize', close);
  window.addEventListener('scroll', onScroll, { capture: true, passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener('pointerdown', onOutside, true);
  window.removeEventListener('keydown', onKey);
  window.removeEventListener('resize', close);
  window.removeEventListener('scroll', onScroll, { capture: true });
});
</script>

<template>
  <Teleport to="body">
    <div v-if="block" ref="el" class="popover" role="dialog" aria-label="Edit block" :style="{ top: `${pos.top}px`, left: `${pos.left}px` }">
      <div class="head">
        <span class="project">{{ project.name }}</span>
        <span class="range">{{ formatRange(block.start, block.span) }}</span>
      </div>
      <p v-if="dates" class="dates">{{ dates }}</p>

      <div class="phases" role="listbox" aria-label="Phase">
        <button
          v-for="phase in roadmap.phases"
          :key="phase.id"
          type="button"
          class="phase"
          :class="{ current: block.phaseId === phase.id }"
          role="option"
          :aria-selected="block.phaseId === phase.id"
          :style="phaseVars(phase.color)"
          @click="setPhase(phase.id)"
        >
          <span class="dot" />
          <span class="phase-name">{{ phase.name }}</span>
          <Icon v-if="block.phaseId === phase.id" name="check" :size="14" />
        </button>
        <p v-if="!roadmap.phases.length" class="none">This roadmap has no phases yet.</p>
        <button type="button" class="link" @click="editPhases">
          <Icon name="settings" :size="13" />
          {{ roadmap.phases.length ? 'Edit phases' : 'Add phases' }}
        </button>
      </div>

      <div class="foot">
        <button type="button" class="action" @click="editProject">
          <Icon name="edit" :size="14" />
          Edit project
        </button>
        <button type="button" class="action action--danger" @click="remove">
          <Icon name="trash" :size="14" />
          Delete block
        </button>
      </div>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
.dates {
  margin: 0;
  padding: 8px 14px 0;
  font-size: 12px;
  color: var(--text-3);
}

.popover {
  position: fixed;
  z-index: 900;
  width: 260px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: var(--surface);
  box-shadow: var(--shadow-pop);
  animation: pop 0.14s ease-out;
}

@keyframes pop {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
}

.head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 14px 10px;
  border-bottom: 1px solid var(--line);
}

.project {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 600;
}

.range {
  flex-shrink: 0;
  font-size: 12px;
  color: var(--text-3);
  font-variant-numeric: tabular-nums;
}

.phases {
  display: flex;
  flex-direction: column;
  max-height: 260px;
  overflow-y: auto;
  padding: 6px;
}

.phase {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 32px;
  padding: 0 8px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  font-size: 13px;
  text-align: left;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: var(--bg);
    outline: none;
  }

  &.current {
    background: color-mix(in srgb, var(--phase) 12%, var(--mix-base));
    color: var(--phase-text);
    font-weight: 600;
  }
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 3px;
  background: var(--phase);
  flex-shrink: 0;
}

.phase-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.none {
  padding: 6px 8px;
  font-size: 12px;
  color: var(--text-3);
}

.link {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
  height: 30px;
  padding: 0 8px;
  border: 0;
  background: none;
  color: var(--text-3);
  font-size: 12px;
  cursor: pointer;

  &:hover {
    color: var(--iris-strong);
  }
}

.foot {
  display: flex;
  justify-content: space-between;
  padding: 6px;
  border-top: 1px solid var(--line);
}

.action {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 10px;
  border: 0;
  border-radius: 7px;
  background: none;
  color: var(--text-2);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: var(--bg);
    color: var(--text);
  }

  &--danger {
    color: var(--danger);

    &:hover {
      background: var(--danger-soft);
      color: var(--danger);
    }
  }
}
</style>
